import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { ArrowUpRight, MapPin, Calendar, Fish, TreePine } from "lucide-react";

const HERO = "/media/hero-poster-hd.jpg";
const HERO_VIDEO_MP4 = "/media/intro.mp4";
const HERO_VIDEO_WEBM = "/media/intro.webm";
const LAKE_IMG = "https://images.unsplash.com/photo-1770130870606-db8e48d9777e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxzZXJlbmUlMjBsYWtlJTIwcmVzb3J0JTIwbmF0dXJlfGVufDB8fHx8MTc3ODk0MDYyNHww&ixlib=rb-4.1.0&q=85";
const CABIN_IMG = "https://images.unsplash.com/photo-1639405791326-b1168dd7ad71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHx3b29kZW4lMjBjYWJpbiUyMGxha2V8ZW58MHx8fHwxNzc4OTQwNjI0fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t, lang, pick } = useLang();
  const [lakes, setLakes] = useState([]);
  const [catches, setCatches] = useState([]);
  const [stats, setStats] = useState({ spots: 0, houses: 0, species: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [l, c, items] = await Promise.all([
          api.get("/lakes"),
          api.get("/catches", { params: { limit: 4 } }),
          api.get("/items"),
        ]);
        setLakes(l.data);
        setCatches(c.data);
        const spotsCount = items.data.filter((i) => i.type === "spot").length;
        const housesCount = items.data.filter((i) => i.type === "house").length;
        const sp = new Set();
        l.data.forEach((lk) => (lk.fish_species_en || []).forEach((s) => sp.add(s)));
        setStats({ spots: spotsCount, houses: housesCount, species: sp.size });
      } catch { /* ignore */ }
    })();
  }, [lang]);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[90vh] min-h-[640px] overflow-hidden grain">
        <video
          poster={HERO}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-drone-video"
        >
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
          <source src={HERO_VIDEO_MP4} type="video/mp4" />
        </video>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-deg h-full flex flex-col justify-end pb-16 md:pb-24 text-bg-paper">
          <div className="max-w-3xl">
            <div className="overline text-bg-paper/80 fade-in" data-testid="hero-overline">{t("home.tagline")}</div>
            <h1 className="font-serif font-light tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-6 text-balance text-bg-paper fade-in-2">
              {t("home.title_1")}<br /><em className="not-italic text-accent">{t("home.title_2")}</em>
            </h1>
            <p className="mt-8 text-lg max-w-xl text-bg-paper/85 fade-in-3 leading-relaxed">{t("home.intro")}</p>
            <div className="flex flex-wrap gap-4 mt-10 fade-in-4">
              <Link to="/booking" className="btn-primary" data-testid="hero-book-cta">
                {t("home.cta_book")} <ArrowUpRight size={16} strokeWidth={1.5} className="ml-2" />
              </Link>
              <Link to="/catches" className="text-sm text-bg-paper/90 hover:text-accent py-3 transition-colors flex items-center gap-2" data-testid="hero-catches-cta">
                {t("home.cta_catches")} <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-line bg-bg-paper">
        <div className="container-deg grid grid-cols-2 md:grid-cols-4 divide-x divide-line">
          {[
            { v: lakes.length || 2, l: t("home.stats_lakes"), icon: TreePine },
            { v: stats.spots, l: t("home.stats_spots"), icon: MapPin },
            { v: stats.houses, l: t("home.stats_houses"), icon: Calendar },
            { v: stats.species, l: t("home.stats_species"), icon: Fish },
          ].map((s, i) => (
            <div key={i} className="py-8 px-6 flex items-center gap-4">
              <s.icon strokeWidth={1.25} size={28} className="text-accent" />
              <div>
                <div className="font-serif text-3xl text-primary">{s.v}</div>
                <div className="overline mt-1">{s.l}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lakes */}
      <section className="py-20 md:py-32 container-deg">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
          <div className="max-w-xl">
            <div className="overline">{t("home.featured_lakes")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-balance">{t("home.featured_lakes_sub")}</h2>
          </div>
          <Link to="/lakes" className="text-sm text-primary hover:text-accent flex items-center gap-2" data-testid="lakes-view-all">
            {t("home.view_all")} <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {(lakes.length ? lakes : [{}, {}]).slice(0, 2).map((lk, i) => (
            <Link key={lk.id || i} to="/lakes" className="card-deg group block overflow-hidden" data-testid={`home-lake-card-${i}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={lk.image_base64 || (i === 0 ? LAKE_IMG : CABIN_IMG)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <div className="overline">{i === 0 ? "Lake 01" : "Lake 02"}</div>
                <h3 className="font-serif text-3xl mt-2">{pick(lk, "name") || (i === 0 ? "North Lake" : "South Lake")}</h3>
                <p className="text-ink-soft mt-4 leading-relaxed line-clamp-3">{pick(lk, "description") || "Loading…"}</p>
                <div className="flex gap-6 mt-6 text-xs text-secondary">
                  <span>{lk.area_hectares || "—"} {t("lakes.hectares")}</span>
                  <span>{lk.max_depth_m || "—"} {t("lakes.meters")}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Catches */}
      <section className="py-20 md:py-32 bg-bg-paper border-y border-line">
        <div className="container-deg">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div className="max-w-xl">
              <div className="overline">{t("home.featured_catches")}</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 text-balance">{t("home.featured_catches_sub")}</h2>
            </div>
            <Link to="/catches" className="text-sm text-primary hover:text-accent flex items-center gap-2" data-testid="catches-view-all">
              {t("home.view_all")} <ArrowUpRight size={14} />
            </Link>
          </div>
          {catches.length === 0 ? (
            <p className="text-ink-soft text-center py-16">{t("catches.no_catches")}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {catches.map((c, i) => (
                <div key={c.id} className="card-deg p-0 overflow-hidden" data-testid={`home-catch-${i}`}>
                  <div className="aspect-square bg-bg-muted overflow-hidden">
                    {c.image_base64 ? (
                      <img src={c.image_base64} alt={c.species_en} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-secondary">
                        <Fish size={42} strokeWidth={1} />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-serif text-xl">{pick(c, "species")}</div>
                    <div className="text-xs text-ink-soft mt-1">{c.weight_kg} {t("common.kg")} · {c.user_name}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
